<template>
  <b-card class="border">
    <div v-if="image.src">
      <Cropper class="cropper" ref="cropper" :src="image.src"
               stencil-component="circle-stencil"
               :canvas="{ maxWidth: 8192, maxHeight: 8192, maxArea: 8192 * 8192,}"
      ></Cropper>
      <div class="btn-group d-flex w-100 mt-2" role="group">
        <div class="btn btn-secondary btn-sm" @click="resetCropper">Cancel</div>
        <div class="btn btn-warning btn-sm" @click="rotate">Rotate</div>
        <div class="btn btn-primary btn-sm" @click="onAvatarSelected">Crop</div>
      </div>
    </div>

    <div class="row justify-content-center" v-else>
      <div class="d-flex justify-content-center my-1">
        <div class="rounded-lg profile-background p-1">
          <b-img rounded="circle" width="150" height="150" style="border: solid 4px white;"
                 alt="Profile Image"
                 :src="avatar"/>
        </div>
      </div>
      <div class="d-flex justify-content-center my-1 col-md-3 col-sm-3">
        <b-card-body>
          <i class="fa fa-3x fa-spin fa-spinner ml-1 mt-2 mb-2" v-if="isLoading"></i>
          <div class="row">
            <VueUploadComponent
              v-if="!imagePresent"
              v-bind="fileUploadOptions"
              v-model='fileAvatar'
              @input-filter="inputFilter"
              ref="upload"
              alt="Add a profile photo"
              title="Add a profile photo"
              class="btn btn-primary mb-1"
            >
              <i class="fa fa-plus-square" aria-hidden="true" title="Add Profile Photo"></i>
            </VueUploadComponent>
            <VueUploadComponent
              v-if="imagePresent"
              v-bind="fileUploadOptions"
              v-model='fileAvatar'
              @input-filter="inputFilter"
              ref="upload"
              alt="Change profile photo"
              title="Change profile photo"
              class='btn btn-secondary ml-1 mb-1'
            >
              <i class="fa fa-pencil" aria-hidden="true" title="Change Profile Photo"></i>
            </VueUploadComponent>
            <b-btn v-if="imagePresent" class="ml-1 mb-1" variant="danger" title="Remove Profile Photo"
                   @click="removeAvatar">
              <i class="fa fa-trash" aria-hidden="true"></i>
            </b-btn>
          </div>
        </b-card-body>
      </div>
      <div class='text-danger' v-if='fileTypeIssue'>Profile image must be a .jpg, .jpeg, or .png</div>
      <div class='text-danger' v-if='fileSizeIssue'>Profile image must be less than 10MB</div>
    </div>
    <div class="text-muted small mt-2 mb-2">With CitSci Since: {{ displayDate(memberSince) }}</div>
  </b-card>
</template>

<script>

function getMimeType(file, fallback = null) {
  const byteArray = (new Uint8Array(file)).subarray(0, 4);
  let header = '';
  for (let i = 0; i < byteArray.length; i++) {
    header += byteArray[i].toString(16);
  }
  switch (header) {
    case "89504e47":
      return "image/png";
    case "47494638":
      return "image/gif";
    case "ffd8ffe0":
    case "ffd8ffe1":
    case "ffd8ffe2":
    case "ffd8ffe3":
    case "ffd8ffe8":
      return "image/jpeg";
    default:
      return fallback;
  }
}

import {mapGetters, mapActions} from 'vuex'
import {displayDate} from "../../../../helpers/date_format_helper";
import VueUploadComponent from 'vue-upload-component'
import {Cropper} from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';

export default {
  components: {
    VueUploadComponent,
    Cropper,
  },
  data() {
    return {
      rounded: false,
      fileUploadOptions: {
        multiple: false,
        drop: false,
        accept: 'image/png,image/jpeg',
        extensions: this.avatarExtensions,
        size: 1024 * 1024 * 10,
        valueDeterminate: 100,
        imageUploadedSuccessfully: false,
        showUploadedImage: 0
      },
      fileAvatar: [],
      fileTypeIssue: false,
      fileSizeIssue: false,
      image: {
        src: null,
        type: null
      }
    }
  },
  created() {
    this.get()
  },
  watch: {
    fileAvatar(newVal, oldVal) {
      if (newVal) {
        // If a file is uploaded, put it through the cropper
        if (newVal[0]) {
          if (this.image.src) {
            URL.revokeObjectURL(this.image.src)
          }
          // 2. Create the blob link to the file to optimize performance:
          const blob = URL.createObjectURL(newVal[0].file);

          // Create a new FileReader to read this image binary data
          const reader = new FileReader();
          // Define a callback function to run, when FileReader finishes its job
          reader.onload = (e) => {
            this.image = {
              // Set the image source
              src: blob,
              // Determine the image type
              type: newVal[0].type,
            };
          };
          // Start the reader job - read file as a data url (base64 format)
          reader.readAsArrayBuffer(newVal[0].file);
        }
      }
    },
  },
  computed: {
    ...mapGetters({
      isLoading: 'user/profile/avatar/isLoading',
      avatar: 'user/profile/avatar/image',
      imagePresent: 'user/profile/avatar/imagePresent',
      avatarExtensions: 'user/profile/avatar/avatarExtensions',
      memberSince: 'user/profile/memberSince',
    }),
  },
  methods: {
    ...mapActions({
      get: 'user/profile/avatar/get',
      upload: 'user/profile/avatar/upload',
      remove: 'user/profile/avatar/remove',
    }),
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        let fileTypeIssue = false;
        let fileSizeIssue = false;
        if (!/\.(gif|jpg|jpeg|png|webp)$/i.test(newFile.name)) {
          fileTypeIssue = true;
        }
        if (newFile.size > 10 * 1000000) {
          fileSizeIssue = true;

        }
        if (fileTypeIssue || fileSizeIssue) {
          this.fileTypeIssue = (fileTypeIssue || this.fileTypeIssue);
          this.fileSizeIssue = (fileSizeIssue || this.fileSizeIssue);
          return prevent()
        }
      }
      if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
      }
    },
    onAvatarSelected() {
      const {canvas} = this.$refs.cropper.getResult();
      if (canvas) {
        canvas.toBlob(blob => {
          this.upload({
            file: blob,
            name: this.fileAvatar[0].name,
            type: this.fileAvatar[0].type,
          }).then(val => {
            if (val) {
              this.resetCropper()
            }
          })
        });
      }
    },
    rotate() {
      this.$refs.cropper.rotate(90);
    },
    resetCropper() {
      this.image.src = null
      this.image.type = null
    },
    removeAvatar() {
      this.remove()
    },
  },
}
</script>

<style scoped lang="scss">
.user-profile {
  background-color: rgba(34, 37, 44, .9);
  text-align: center;
}

.profile-avatar {
  border: 2px solid #FFFFFF;
  width: 21px;
  height: 21px;
  overflow: hidden;
  border-radius: 50%;
  margin-right: 5px;
  max-width: 100%;
}

.profile-background {
  background-color: #C0C0C0;
}

.cropper {
  max-height: 350px;
  width: 100%;
  background-color: #DDD;
}
</style>
